/**
 * Step 2 — Timeline.
 *
 * The phases and their standard activity descriptions come from the approved
 * template and are shown read-only. Sales enters the duration and nothing else,
 * which is the whole point: nobody retypes "Stakeholder interviews, initiative
 * inventory, gap analysis".
 *
 * Phases the template marks ONGOING or CADENCE carry approved wording of their
 * own, so they get a different control — a week count would be wrong for
 * "Steady-State Support".
 */
import type { PaymentTerm, ProposalConfiguration, TemplateManifest } from '../../lib/api';
import { Card, Input, Select } from '../../components/ui';

const CADENCE_OPTIONS = ['Monthly', 'Quarterly', 'Monthly / Quarterly'];

export function TimelineStep({
  config,
  update,
  disabled,
  flagged,
  manifest,
  allowActivityEditing,
}: {
  config: ProposalConfiguration;
  update<K extends keyof ProposalConfiguration>(key: K, value: ProposalConfiguration[K]): void;
  disabled: boolean;
  flagged: Set<string>;
  manifest: TemplateManifest;
  paymentTerms?: PaymentTerm[];
  /** Customize mode also allows the standard activity wording to be edited. */
  allowActivityEditing?: boolean;
}) {
  const durations = config.durations ?? {};

  // Phase names and activity wording, when Customize has materialised them.
  const phaseItems = config.timelinePhases;
  const activityFor = (id: string, fallback: string) =>
    phaseItems?.find((item) => item.id === id)?.detail ?? fallback;
  const phaseNameFor = (id: string, fallback: string) =>
    phaseItems?.find((item) => item.id === id)?.value ?? fallback;
  const isModified = (id: string) =>
    phaseItems?.find((item) => item.id === id)?.state === 'MODIFIED';

  /** Materialise on first edit so a Quick Proposal never carries item state. */
  const ensureMaterialised = () => {
    if (phaseItems) return phaseItems;
    const created = manifest.timelinePhases.map((phase) => ({
      id: phase.id,
      standardValue: phase.phase,
      value: phase.phase,
      standardDetail: phase.activities,
      detail: phase.activities,
      state: 'STANDARD' as const,
      included: true,
    }));
    update('timelinePhases', created);
    return created;
  };

  const setActivity = (id: string, detail: string) => {
    const items = ensureMaterialised();
    update(
      'timelinePhases',
      items.map((item) => (item.id === id ? { ...item, detail } : item)),
    );
  };

  const restorePhase = (id: string) => {
    if (!phaseItems) return;
    update(
      'timelinePhases',
      phaseItems.map((item) =>
        item.id === id
          ? { ...item, value: item.standardValue ?? item.value, detail: item.standardDetail ?? '' }
          : item,
      ),
    );
  };

  const setDuration = (id: string, value: number | string | undefined) => {
    const next = { ...durations };
    if (value === undefined || value === '') delete next[id];
    else next[id] = value;
    update('durations', next);
  };

  return (
    <Card
      title="Indicative timeline"
      description="Phases and activities come from the approved template. Enter the duration."
    >
      <div className="-mx-5 overflow-x-auto">
        <table className="w-full min-w-[640px] border-collapse text-[13px]">
          <thead>
            <tr className="border-b border-rule text-left text-[11px] uppercase tracking-wide text-muted">
              <th scope="col" className="px-5 py-2.5 font-semibold">
                Phase
              </th>
              <th scope="col" className="px-3 py-2.5 font-semibold">
                Standard activity
              </th>
              <th scope="col" className="w-56 px-5 py-2.5 font-semibold">
                Duration
              </th>
            </tr>
          </thead>
          <tbody>
            {manifest.timelinePhases.map((phase) => {
              const isFlagged = flagged.has(`durations.${phase.id}`);
              const value = durations[phase.id];
              return (
                <tr key={phase.id} className="border-b border-rule last:border-0 align-top">
                  <td className="px-5 py-3 font-medium text-ink">
                    {phaseNameFor(phase.id, phase.phase)}
                  </td>
                  <td className="px-3 py-3 text-body">
                    {allowActivityEditing ? (
                      <div className="space-y-1">
                        <textarea
                          rows={2}
                          maxLength={600}
                          disabled={disabled}
                          aria-label={`Standard activity for ${phase.phase}`}
                          value={activityFor(phase.id, phase.activities)}
                          onChange={(event) => setActivity(phase.id, event.target.value)}
                          className="w-full rounded-md border border-rule bg-white px-2 py-1.5 text-[13px] text-ink focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20 disabled:bg-tint"
                        />
                        {isModified(phase.id) && (
                          <div className="flex items-center gap-2">
                            <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800 ring-1 ring-amber-200">
                              Modified
                            </span>
                            <button
                              type="button"
                              disabled={disabled}
                              onClick={() => restorePhase(phase.id)}
                              className="text-[11px] font-medium text-teal hover:underline"
                            >
                              Restore standard
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      phase.activities
                    )}
                  </td>
                  <td className="px-5 py-3">
                    {phase.durationKind === 'DURATION' ? (
                      <div>
                        <div className="flex items-center gap-2">
                          <Input
                            id={`duration-${phase.id}`}
                            type="number"
                            min={1}
                            max={520}
                            inputMode="numeric"
                            aria-label={`Duration in weeks for ${phase.phase}`}
                            aria-invalid={isFlagged}
                            className={isFlagged ? 'border-red-400' : undefined}
                            value={typeof value === 'number' ? String(value) : ''}
                            disabled={disabled}
                            onChange={(event) =>
                              setDuration(
                                phase.id,
                                event.target.value === '' ? undefined : Number(event.target.value),
                              )
                            }
                          />
                          <span className="shrink-0 text-[13px] text-muted">
                            {value === 1 ? 'week' : 'weeks'}
                          </span>
                        </div>
                        {isFlagged && (
                          <p role="alert" className="mt-1 text-[12px] text-red-700">
                            Required
                          </p>
                        )}
                      </div>
                    ) : phase.durationKind === 'CADENCE' ? (
                      <Select
                        id={`duration-${phase.id}`}
                        aria-label={`Cadence for ${phase.phase}`}
                        value={typeof value === 'string' ? value : 'Monthly'}
                        disabled={disabled}
                        onChange={(event) => setDuration(phase.id, event.target.value)}
                      >
                        {CADENCE_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </Select>
                    ) : (
                      <div>
                        <span className="inline-flex rounded bg-tint px-2 py-1 text-[12px] font-medium text-body">
                          {typeof value === 'string' && value ? value : 'Ongoing'}
                        </span>
                        <p className="mt-1 text-[11px] text-muted">Approved wording</p>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="mt-4 text-[12px] text-muted">
        The approved template prints: “Exact timing is confirmed during scoping. A typical
        engagement of this type runs as follows.”
      </p>
    </Card>
  );
}
